import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  FaBell, FaBellSlash, 
  FaTimes, FaSave, FaCheck, FaPlus, FaMapMarkerAlt, FaSpinner,
  FaInfoCircle, FaFilter, FaSearch
} from 'react-icons/fa';
import {
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Badge,
  Button,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Alert,
  Progress,
  Card,
  CardBody
} from 'reactstrap';
import useUserPreferences from './preferencias';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabaseClient';
import { membershipDisplay } from './membershipDisplay';
import { hasMenuAccess, nombrePlan } from './planPermissions';
import useAccesosPortal from '../hooks/useAccesosPortal';
import './account-design21.scss';
import { Building2, Award, Settings2, ChevronRight, LogOut, ArrowUp, ReceiptText, BellRing, BellOff, Lightbulb, Bookmark, IdCard } from 'lucide-react';
import MiCuenta31 from './MiCuenta31';
import './user-dropdown.css';
import './user-preferences.css';

let guiaPreferenciasAbierta = false;

/* Interruptor de las preferencias por tipo de proceso. Apagado no es lo mismo que
   deshabilitado: deshabilitado significa que el interruptor maestro está en NO, y ahí
   sigue mostrando la preferencia guardada, que no se toca. */
const SwitchPref = ({ activo, onToggle, disabled }) => (
  <div
    role="switch"
    aria-checked={activo}
    aria-disabled={disabled || undefined}
    onClick={() => { if (!disabled) onToggle(); }}
    style={{
      width: '52px',
      height: '28px',
      borderRadius: '14px',
      background: activo ? '#0f766e' : '#d1d5db',
      position: 'relative',
      cursor: disabled ? 'not-allowed' : 'pointer',
      transition: 'background 0.2s ease',
      flexShrink: 0
    }}
  >
    <div style={{
      width: '24px',
      height: '24px',
      borderRadius: '50%',
      background: 'white',
      position: 'absolute',
      top: '2px',
      left: activo ? '26px' : '2px',
      transition: 'left 0.2s ease',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    }} />
  </div>
);

const UserDropdown = ({ user, onLogout, onOpenPlans, onRenew, pasoGuia = 0, onAbrirMenuGuiado, onAbrirPreferenciasGuiadas, onTerminarRecorrido }) => {
  const navigate = useNavigate();
  const [trackingCount, setTrackingCount] = useState(null);
  const { 
    formattedData, 
    loading: dbLoading, 
    error: dbError, 
    savePreferences, 
    loadUserData 
  } = useUserPreferences(user?.email);

  // Catálogo de grupos del plan Curso: decide si este usuario tiene SEACE y, con
  // ello, si se le ofrece el correo de Contratos Mayores.
  const { grupos: gruposCurso } = useAccesosPortal();

  const [preferencesModalOpen, setPreferencesModalOpen] = useState(false);
  const [cuentaModalOpen, setCuentaModalOpen] = useState(false);
  const [emailNotifications, setEmailNotifications] = useState(true);
  // Contratos menores: la hora elegida se guarda aparte del "no enviar", para devolverla
  // tal cual cuando el usuario vuelva a activar el correo.
  const [recibeMenores, setRecibeMenores] = useState(true);
  const [horasMenores, setHorasMenores] = useState(['22']); // ['13'] | ['22'] | ambas
  const [seaceCorreo, setSeaceCorreo] = useState(true); // flag_seace
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [keywordInput, setKeywordInput] = useState('');
  const [keywords, setKeywords] = useState([]);
  const [generalKeywordSearch, setGeneralKeywordSearch] = useState(false);
  const [similarSearch, setSimilarSearch] = useState(false);
  const [departmentInput, setDepartmentInput] = useState('');
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [savedSuccessfully, setSavedSuccessfully] = useState(false);
  const [guardadoParcial, setGuardadoParcial] = useState(false); // faltó la migración
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('notifications');
  const [pasoRecorrido, setPasoRecorrido] = useState(0);

  useEffect(() => {
    if (pasoGuia === 1) guiaPreferenciasAbierta = false;
    if (pasoGuia !== 3 || guiaPreferenciasAbierta) return;
    guiaPreferenciasAbierta = true;
    setActiveTab('notifications');
    setPasoRecorrido(1);
    setPreferencesModalOpen(true);
  }, [pasoGuia]);

  // Cargar datos iniciales
  useEffect(() => {
    if (formattedData) {
      setEmailNotifications(formattedData.flagCorreo);

      // '' = no recibe menores; cualquier otro caso deja marcada una hora válida.
      const horaGuardada = String(formattedData.horaEnvio ?? '22');
      const horasValidas = horaGuardada.split(',').filter(h => h === '13' || h === '22');
      setRecibeMenores(horasValidas.length > 0);
      if (horasValidas.length > 0) setHorasMenores(horasValidas.sort());
      setSeaceCorreo(formattedData.flagSeace !== 'NO');
      
      const tiposFromDB = formattedData.tipos;
      if (Array.isArray(tiposFromDB)) {
        setSelectedTypes(tiposFromDB);
      } else if (typeof tiposFromDB === 'string') {
        if (tiposFromDB === 'ALL') {
          setSelectedTypes(['ALL']);
        } else if (tiposFromDB === '') {
          setSelectedTypes([]);
        } else {
          setSelectedTypes(tiposFromDB.split(',').filter(Boolean));
        }
      } else {
        setSelectedTypes([]);
      }
      
      const palabrasFromDB = formattedData.palabrasClaves;
      if (Array.isArray(palabrasFromDB)) {
        setKeywords(palabrasFromDB);
      } else if (typeof palabrasFromDB === 'string') {
        if (palabrasFromDB === 'ALL') {
          setKeywords([]);
          setGeneralKeywordSearch(true);
        } else if (palabrasFromDB === '') {
          setKeywords([]);
        } else {
          setKeywords(palabrasFromDB.split(',').filter(Boolean));
        }
      } else {
        setKeywords([]);
      }
      
      const deptsFromDB = formattedData.departamentos;
      if (Array.isArray(deptsFromDB)) {
        setSelectedDepartments(deptsFromDB);
      } else if (typeof deptsFromDB === 'string') {
        if (deptsFromDB === 'ALL' || deptsFromDB === '') {
          setSelectedDepartments(['ALL']);
        } else {
          setSelectedDepartments(deptsFromDB.split(',').filter(Boolean));
        }
      } else {
        setSelectedDepartments([]);
      }
      
      setSimilarSearch(formattedData.busquedaSimilar === 'SI');
    }
  }, [formattedData?.correo]);

  const toggleCuentaModal = useCallback(() => setCuentaModalOpen(abierto => !abierto), []);

  const togglePreferencesModal = useCallback(() => {
    const newState = !preferencesModalOpen;
    setPreferencesModalOpen(newState);
    
    if (!newState) {
      setSavedSuccessfully(false);
      setGuardadoParcial(false);
      setSaving(false);
      setActiveTab('notifications');
      setPasoRecorrido(0);
      onTerminarRecorrido?.();
    }
  }, [preferencesModalOpen, onTerminarRecorrido]);

  // Los nombres salen de planPermissions, que es donde viven los códigos: un
  // alumno del plan Curso ve el plan al que equivale su grupo, igual que en la
  // barra superior y en el sidebar.
  const getPlanDisplayName = useCallback(
    (plan) => nombrePlan(plan, formattedData?.cursoTipo || user?.curso_tipo, gruposCurso),
    [formattedData?.cursoTipo, user?.curso_tipo, gruposCurso]
  );

  const departments = useMemo(() => [
    'AMAZONAS', 'ANCASH', 'APURÍMAC', 'AREQUIPA', 'AYACUCHO', 'CAJAMARCA',
    'CALLAO', 'CUSCO', 'HUANCAVELICA', 'HUÁNUCO', 'ICA', 'JUNÍN',
    'LA LIBERTAD', 'LAMBAYEQUE', 'LIMA', 'LORETO', 'MADRE DE DIOS',
    'MOQUEGUA', 'PASCO', 'PIURA', 'PUNO', 'SAN MARTÍN', 'TACNA',
    'TUMBES', 'UCAYALI'
  ], []);

  const handleTypeSelection = useCallback((type) => {
    setSelectedTypes(prev => {
      if (type === 'ALL') {
        if (prev.includes('ALL')) {
          return [];
        }
        return ['ALL'];
      }
      
      const withoutAll = prev.filter(t => t !== 'ALL');
      
      if (withoutAll.includes(type)) {
        return withoutAll.filter(t => t !== type);
      } else {
        return [...withoutAll, type];
      }
    });
  }, []);

  const handleAddKeyword = useCallback(() => {
    const trimmedKeyword = keywordInput.trim();
    if (trimmedKeyword && !keywords.includes(trimmedKeyword)) {
      setKeywords(prev => [...prev, trimmedKeyword]);
      setKeywordInput('');
    }
  }, [keywordInput, keywords]);

  const handleRemoveKeyword = useCallback((keywordToRemove) => {
    setKeywords(prev => {
      const newKeywords = prev.filter(keyword => keyword !== keywordToRemove);
      if (newKeywords.length === 0) {
        setSimilarSearch(false);
      }
      return newKeywords;
    });
  }, []);

  const handleKeywordKeyPress = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddKeyword();
    }
  }, [handleAddKeyword]);

  const handleAddDepartment = useCallback(() => {
    const department = departmentInput.toUpperCase().trim();
    if (department && departments.includes(department) && !selectedDepartments.includes(department)) {
      setSelectedDepartments(prev => [...prev, department]);
      setDepartmentInput('');
    }
  }, [departmentInput, departments, selectedDepartments]);

  const handleRemoveDepartment = useCallback((deptToRemove) => {
    setSelectedDepartments(prev => prev.filter(dept => dept !== deptToRemove));
  }, []);

  const handleDepartmentKeyPress = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddDepartment();
    }
  }, [handleAddDepartment]);

  const getDepartmentSuggestions = useMemo(() => {
    if (!departmentInput.trim()) return [];
    return departments
      .filter(dept => 
        dept.toLowerCase().includes(departmentInput.toLowerCase()) &&
        !selectedDepartments.includes(dept)
      )
      .slice(0, 5);
  }, [departmentInput, selectedDepartments, departments]);

  const handleNotificationToggle = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setEmailNotifications(prev => !prev);
  }, []);

  // El correo del SEACE solo se ofrece a los planes que tienen acceso al SEACE: para el
  // resto, el interruptor maestro ya decide todo lo que reciben.
  const planUsuario = String(formattedData?.plan || '').trim().toUpperCase();
  const cursoTipo = formattedData?.cursoTipo || user?.curso_tipo;
  const verSeace = hasMenuAccess(planUsuario, 'SEACE', cursoTipo, gruposCurso)
    || hasMenuAccess(planUsuario, 'SEACE Prueba', cursoTipo, gruposCurso);
  // Sin SEACE no hay nada que distinguir: el interruptor de menores duplicaría al maestro.
  const soloMenores = !verSeace;
  const menoresEncendidos = soloMenores || recibeMenores;

  // Las horas no se borran al apagar el correo de menores: vuelven intactas al reactivarlo.
  const handleRecibeMenores = useCallback(() => {
    setRecibeMenores(actual => !actual);
  }, []);

  // Quitar la última hora marcada equivale a no querer el correo: apaga el interruptor
  // sin perder la marca, así reactivarlo devuelve el horario que tenía.
  const handleHoraMenores = useCallback((hora) => {
    const marcada = horasMenores.includes(hora);
    if (marcada && horasMenores.length === 1) {
      // Quitar la única hora equivale a no querer el correo. Con el interruptor propio a
      // la vista se apaga ahí; sin él, esa decisión es del interruptor maestro.
      if (!soloMenores) setRecibeMenores(false);
      return;
    }
    if (!marcada) setRecibeMenores(true);
    setHorasMenores(marcada
      ? horasMenores.filter(h => h !== hora)
      : [...horasMenores, hora].sort());
  }, [horasMenores, soloMenores])

  const handleSavePreferences = useCallback(async () => {
    setSaving(true);
    setSavedSuccessfully(false);
    
    try {
      let tipoValue = '';
      if (selectedTypes.length === 0) {
        tipoValue = '';
      } else if (selectedTypes.includes('ALL')) {
        tipoValue = 'ALL';
      } else {
        tipoValue = selectedTypes.sort().join(',');
      }
      
      let palabrasValue = '';
      if (generalKeywordSearch) {
        palabrasValue = 'ALL';
      } else if (keywords.length > 0) {
        palabrasValue = keywords.join(',');
      } else {
        palabrasValue = '';
      }
      
      let departamentosValue = '';
      if (selectedDepartments.length > 0) {
        departamentosValue = selectedDepartments.join(',');
      }
      
      const preferencesData = {
        flag_correo: emailNotifications ? 'SI' : 'NO',
        tipo: tipoValue,
        palabras_claves: palabrasValue,
        departamentos: departamentosValue,
        flag_sunat: similarSearch ? 'SI' : 'NO',
        // Nunca null: '' cuando no quiere el correo de menores, si no las horas elegidas.
        hora_envio: menoresEncendidos ? horasMenores.join(',') : '',
        flag_seace: verSeace ? (seaceCorreo ? 'SI' : 'NO') : null
      };
      
      console.log('📤 Datos a guardar:', preferencesData);
      
      const result = await savePreferences(preferencesData);
      
      if (result.success) {
        setGuardadoParcial(!!result.parcial);
        setSavedSuccessfully(true);
        // Un guardado incompleto se queda a la vista: hay algo que resolver en la base.
        if (!result.parcial) {
          setTimeout(() => {
            setSavedSuccessfully(false);
            setPreferencesModalOpen(false);
          }, 2000);
        }
      }
    } catch (error) {
      console.error('Error inesperado:', error);
    } finally {
      setSaving(false);
    }
  }, [emailNotifications, selectedTypes, keywords, selectedDepartments, generalKeywordSearch, similarSearch, menoresEncendidos, horasMenores, seaceCorreo, verSeace, savePreferences]);

  const typeConfig = {
    ALL: { label: 'Todos', icon: '📋', color: '#6366f1' },
    Servicio: { label: 'Servicio', icon: '🔧', color: '#0ea5e9' },
    Bien: { label: 'Bien', icon: '📦', color: '#10b981' },
    Obra: { label: 'Obra', icon: '🏗️', color: '#f59e0b' }
  };

  const getConfigProgress = useMemo(() => {
    let completed = 0;
    const total = 4;
    
    if (emailNotifications) completed++;
    if (selectedTypes.length > 0) completed++;
    if (keywords.length > 0 || generalKeywordSearch) completed++;
    if (selectedDepartments.length > 0) completed++;
    
    return Math.round((completed / total) * 100);
  }, [emailNotifications, selectedTypes, keywords, selectedDepartments, generalKeywordSearch]);

  const tabs = [
    { id: 'notifications', label: 'Notificaciones', icon: <FaBell /> },
    { id: 'filters', label: 'Ubicación', icon: <FaMapMarkerAlt /> },
    { id: 'keywords', label: 'Palabras claves', icon: <FaSearch /> }
  ];

  const membership = membershipDisplay(formattedData || user || {});
  const currentPlan = getPlanDisplayName(formattedData?.plan || user?.plan);
  const isAdmin = String(formattedData?.plan || user?.plan || '').toUpperCase() === 'ADMI';
  const savedList = value => (Array.isArray(value) ? value : String(value || '').split(',')).map(v => String(v).trim()).filter(Boolean);
  const savedTypes = savedList(formattedData?.tipos);
  const savedDepartments = savedList(formattedData?.departamentos);
  const savedKeywords = savedList(formattedData?.palabrasClaves);
  const alertsActive = formattedData?.flagCorreo === true;
  const needsDepartments = !!formattedData && !savedDepartments.length;
  const needsTypes = !!formattedData && !savedTypes.length;
  const refreshTracking = async () => {
    const userId = user?.email || user?.id || user?.user_metadata?.email;
    if (!userId) return;
    try {
      const { data, error } = await supabase.from('calendar_events').select('nomenclatura').eq('user_id', userId);
      setTrackingCount(error ? null : new Set((data || []).map(event => event.nomenclatura)).size);
    } catch { setTrackingCount(null); }
  };

  return (
    <>
      <UncontrolledDropdown nav inNavbar className="nuvia-account">
        <DropdownToggle nav caret className="nuvia-account-toggle" onClick={() => { refreshTracking(); if (pasoGuia === 1) onAbrirMenuGuiado?.(); }} aria-label="Abrir menú de mi cuenta">
          <span className="nuvia-account-avatar">
            {user?.picture ? <img src={user.picture} alt="" /> : (formattedData?.nombre?.charAt(0) || user?.name?.charAt(0) || 'U')}
          </span>
          <span className="d-none d-md-inline nuvia-account-toggle-name">{formattedData?.nombre?.split(' ')[0] || user?.name?.split(' ')[0] || 'Usuario'}</span>
        </DropdownToggle>
        <DropdownMenu end className="nuvia-account-menu account21">
          {pasoGuia === 2 && <div role="status" style={{ margin: '12px 14px', padding: '12px 14px', borderRadius: 10, background: '#eff9f5', border: '1px solid #b9dfd2', color: '#174e43', fontSize: 13, lineHeight: 1.45 }}><strong style={{ display: 'block', marginBottom: 3 }}>Paso 2 de 4 · Abre Preferencias</strong>Haz clic en la opción <strong>Preferencias</strong> para elegir tus alertas y filtros.</div>}
          <div className="account21-profile"><span className="nuvia-account-avatar account21-avatar">{user?.picture ? <img src={user.picture} alt="" /> : (formattedData?.nombre?.charAt(0) || user?.name?.charAt(0) || 'U')}</span><div><strong>{formattedData?.nombre || user?.name || 'Usuario'}</strong><p>{formattedData?.correo || user?.email}</p></div>{isAdmin && <span className="account21-badge">Admin</span>}</div>
          <div className="account21-company"><Building2 size={19} /><div><span className="account21-label">EMPRESA</span><strong>{formattedData?.empresa || (dbLoading ? 'Cargando…' : 'Sin empresa registrada')}</strong></div></div>
          <section className="account21-membership" aria-label="Mi membresía"><div className="account21-membership-row"><Award size={19} /><div><span className="account21-label">MI MEMBRESÍA</span><strong>{currentPlan}</strong></div><div className="account21-days"><strong>{dbLoading ? '…' : membership.days === null ? 'Sin fecha' : membership.days === 0 ? 'Vencida' : membership.days + ' días'}</strong><span>{membership.date ? 'hasta ' + membership.date : 'Vigencia no disponible'}</span></div></div>{membership.progress !== null && <Progress className="account21-progress" value={membership.progress} aria-label="Vigencia de la membresía" />}<div className="account21-membership-actions"><DropdownItem className="account21-button account21-primary" onClick={onOpenPlans}><ArrowUp size={17} />Mejorar plan</DropdownItem><DropdownItem className="account21-button" onClick={() => onRenew?.(currentPlan)}><ReceiptText size={17} />Renovar</DropdownItem></div></section>
          <section className="account21-alerts"><div className="account21-alerts-title"><span className="account21-label">MIS ALERTAS</span><span className={'account21-badge ' + (!alertsActive ? 'account21-muted' : '')}>{alertsActive ? <BellRing size={14} /> : <BellOff size={14} />}{dbLoading ? 'Cargando' : !formattedData ? 'Sin información' : alertsActive ? 'Activas' : 'Pausadas'}</span></div>{formattedData && <div className="account21-chips">{savedTypes.map(type => <span key={type}>{type === 'ALL' ? 'Todos los objetos' : type}</span>)}{savedDepartments.length > 0 && <span>{savedDepartments.includes('ALL') ? 'Todo el Perú' : savedDepartments.join(' · ')}</span>}<span>{savedKeywords.includes('ALL') ? 'Todas las palabras' : savedKeywords.length + ' palabras clave'}</span></div>}{(needsDepartments || needsTypes) && <div className="account21-tip"><Lightbulb size={18} /><span>Te falta elegir {needsDepartments ? 'departamentos' : 'tipos de contratación'} para afinar tus correos.</span><DropdownItem onClick={() => { togglePreferencesModal(); setActiveTab(needsDepartments ? 'filters' : 'notifications'); }}>Configurar</DropdownItem></div>}</section>
          {dbLoading && <div className="nuvia-account-status" role="status"><FaSpinner className="fa-spin" />Cargando información…</div>}{dbError && <div className="nuvia-account-error" role="alert">{dbError}</div>}
          <div className="account21-actions"><DropdownItem onClick={toggleCuentaModal} className="account21-action"><span className="account21-icon"><IdCard size={19} /></span><span><strong>Mi cuenta</strong><small>Nombre, empresa y contraseña</small></span><ChevronRight size={16} /></DropdownItem><DropdownItem onClick={() => { togglePreferencesModal(); if (pasoGuia === 2) onAbrirPreferenciasGuiadas?.(); }} className={`account21-action${pasoGuia === 2 ? ' nuvia-guide-preferences' : ''}`} style={pasoGuia === 2 ? { outline: '2px solid #0f766e', outlineOffset: '-2px', background: '#eff9f5', borderRadius: 10 } : undefined}><span className="account21-icon"><Settings2 size={19} /></span><span><strong>Preferencias</strong><small>Notificaciones, ubicación y palabras clave</small></span><ChevronRight size={16} /></DropdownItem><DropdownItem onClick={() => navigate('/Calendario')} className="account21-action"><span className="account21-icon"><Bookmark size={19} /></span><span><strong>Mi seguimiento</strong><small>{trackingCount === null ? 'Ver procesos guardados' : trackingCount + ' procesos guardados'}</small></span><ChevronRight size={16} /></DropdownItem><DropdownItem divider /><DropdownItem onClick={onLogout} className="account21-action account21-logout"><span className="account21-icon"><LogOut size={19} /></span><span><strong>Cerrar sesión</strong></span></DropdownItem></div>
        </DropdownMenu>
      </UncontrolledDropdown>

      <Modal zIndex={12000}
        className="nuvia-preferences"
        labelledBy="nuvia-preferences-title"
        isOpen={preferencesModalOpen} 
        toggle={togglePreferencesModal} 
        size="lg" 
        centered
        backdrop="static"
        keyboard={!saving}
        style={{ maxWidth: '720px' }}
      >
        <ModalHeader 
          toggle={!saving ? togglePreferencesModal : undefined}
          style={{ 
            background: 'white', 
            borderBottom: '1px solid #f3f4f6',
            padding: '24px 32px'
          }}
        >
          <div>
            <h5 id="nuvia-preferences-title" className="mb-1" style={{ fontWeight: '600', fontSize: '1.125rem', color: '#111827' }}>
              Configuración de preferencias
            </h5>
            <small style={{ color: '#6b7280', fontSize: '0.875rem' }}>
              Personaliza tus notificaciones y filtros
            </small>
          </div>
        </ModalHeader>
        
        <ModalBody style={{ padding: '0', background: '#fafafa' }}>
          {pasoRecorrido > 0 && <div style={{ position: 'relative', zIndex: 13003, margin: '20px 32px 0', padding: '16px 18px', borderRadius: 12, border: '1px solid #b9dfd2', background: '#eff9f5', color: '#174e43' }}>
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.05em', marginBottom: 5 }}>CONFIGURACIÓN · PASO {pasoRecorrido} DE 2</div>
            <div style={{ fontWeight: 650, marginBottom: 4 }}>{pasoRecorrido === 1 ? 'Elige qué alertas quieres recibir' : 'Afina tus convocatorias'}</div>
            <div style={{ fontSize: 13, lineHeight: 1.5 }}>{pasoRecorrido === 1 ? 'Activa o pausa las alertas por correo y configura los envíos que quieras recibir.' : 'En esta sección puedes elegir tipos de contratación, regiones y palabras clave para personalizar los resultados.'}</div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
              <Button size="sm" color="link" style={{ color: '#42645b', textDecoration: 'none' }} onClick={() => { setPasoRecorrido(0); onTerminarRecorrido?.(); }}>Terminar recorrido</Button>
              <Button size="sm" style={{ background: '#0f766e', borderColor: '#0f766e' }} onClick={() => {
                if (pasoRecorrido === 1) { setActiveTab('filters'); setPasoRecorrido(2); }
                else { setPasoRecorrido(0); onTerminarRecorrido?.(); }
              }}>{pasoRecorrido === 1 ? 'Siguiente: filtros' : 'Listo'}</Button>
            </div>
          </div>}
          {savedSuccessfully && (
            <Alert 
              color={guardadoParcial ? 'warning' : 'success'} 
              style={{ 
                margin: '24px 32px 0',
                borderRadius: '12px', 
                border: `1px solid ${guardadoParcial ? '#f3d9a4' : '#bfd8cb'}`,
                background: guardadoParcial ? '#fdf6e7' : '#edf5f1',
                color: guardadoParcial ? '#8a5308' : '#0f4f47',
                padding: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}
            >
              <div style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                background: guardadoParcial ? '#8a5308' : '#0f766e',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                {guardadoParcial
                  ? <FaInfoCircle style={{ fontSize: '0.9375rem', color: 'white' }} />
                  : <FaCheck style={{ fontSize: '0.9375rem', color: 'white' }} />}
              </div>
              <div>
                <strong style={{ fontSize: '0.875rem', display: 'block', marginBottom: '2px' }}>{guardadoParcial ? 'Se guardó solo una parte' : 'Cambios guardados'}</strong>
                <span style={{ fontSize: '0.8125rem' }}>{guardadoParcial
                  ? 'El horario de contratos menores y el correo del SEACE no se guardaron: falta aplicar la migración en Supabase.'
                  : 'Tus preferencias se actualizaron correctamente'}</span>
              </div>
            </Alert>
          )}

          {/* Progress bar */}
          <div className="np-progress" style={{ 
            padding: '20px 32px',
            background: 'white',
            borderBottom: '1px solid #f3f4f6'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '10px'
            }}>
              <span style={{ fontSize: '0.8125rem', fontWeight: '500', color: '#6b7280' }}>
                Configuración completa
              </span>
              <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#111827' }}>
                {getConfigProgress}%
              </span>
            </div>
            <Progress 
              value={getConfigProgress} 
              style={{ height: '6px', borderRadius: '10px', background: '#f3f4f6' }}
              color={getConfigProgress === 100 ? 'success' : 'primary'}
            />
          </div>

          {/* Tabs */}
          <div className="np-tabs" aria-label="Secciones de preferencias" style={{ 
            display: 'flex', 
            borderBottom: '1px solid #e5e7eb',
            padding: '0',
            background: 'white'
          }}>
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  flex: 1,
                  padding: '16px',
                  background: 'none',
                  border: 'none',
                  borderBottom: activeTab === tab.id ? '2px solid #0f766e' : '2px solid transparent',
                  color: activeTab === tab.id ? '#0f766e' : '#6b7280',
                  fontWeight: activeTab === tab.id ? '600' : '500',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  marginBottom: '-1px'
                }}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div id="np-tab-content" className="np-content" style={{ padding: '32px', minHeight: '400px' }}>
            {activeTab === 'notifications' && (
              <div style={{ maxWidth: '600px', margin: '0 auto' }}>
                {/* Toggle de notificaciones */}
                <div className="np-card" style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '24px',
                  border: '1px solid #e5e7eb',
                  marginBottom: '24px'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: '16px'
                  }}>
                    <div style={{ flex: 1 }}>
                      <h6 style={{ 
                        margin: 0, 
                        marginBottom: '4px',
                        color: '#111827', 
                        fontWeight: '600',
                        fontSize: '0.9375rem'
                      }}>
                        Alertas por correo
                      </h6>
                      <p style={{ 
                        margin: 0, 
                        color: '#6b7280', 
                        fontSize: '0.8125rem' 
                      }}>
                        Recibe alertas de nuevas licitaciones
                      </p>
                    </div>
                    
                    <div
                      onClick={handleNotificationToggle}
                      style={{
                        width: '52px',
                        height: '28px',
                        borderRadius: '14px',
                        background: emailNotifications ? '#0f766e' : '#d1d5db',
                        position: 'relative',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        flexShrink: 0
                      }}
                    >
                      <div style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        background: 'white',
                        position: 'absolute',
                        top: '2px',
                        left: emailNotifications ? '26px' : '2px',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                      }} />
                    </div>
                  </div>

                  <div style={{
                    background: emailNotifications ? '#edf5f1' : '#fef2f2',
                    padding: '12px',
                    borderRadius: '8px',
                    border: `1px solid ${emailNotifications ? '#cfe1d6' : '#fecaca'}`,
                    display: 'flex',
                    gap: '10px'
                  }}>
                    <FaInfoCircle style={{ 
                      fontSize: '0.875rem', 
                      color: emailNotifications ? '#0f766e' : '#dc2626',
                      flexShrink: 0,
                      marginTop: '2px'
                    }} />
                    <p style={{ 
                      margin: 0, 
                      fontSize: '0.8125rem',
                      color: emailNotifications ? '#0f4f47' : '#991b1b',
                      lineHeight: '1.4'
                    }}>
                      {emailNotifications 
                        ? 'Configura los filtros para recibir solo las notificaciones relevantes.' 
                        : 'Activa las notificaciones para no perderte oportunidades de negocio.'}
                    </p>
                  </div>
                </div>

                {/* Contratos menores — hora de envío */}
                <div className="np-card" style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '24px',
                  border: '1px solid #e5e7eb',
                  marginBottom: '24px',
                  opacity: emailNotifications ? 1 : 0.55
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '16px',
                    marginBottom: menoresEncendidos ? '16px' : 0
                  }}>
                    <div style={{ flex: 1 }}>
                      <h6 style={{
                        margin: 0,
                        marginBottom: '4px',
                        color: '#111827',
                        fontWeight: '600',
                        fontSize: '0.9375rem'
                      }}>
                        Contratos menores
                      </h6>
                      <p style={{ margin: 0, color: '#6b7280', fontSize: '0.8125rem' }}>
                        {!menoresEncendidos
                          ? 'No recibirás el correo de contratos menores'
                          : horasMenores.length === 2
                            ? 'Dos correos al día: 1:00 p.m. y 10:00 p.m.'
                            : `Un correo al día, a las ${horasMenores[0] === '13' ? '1:00 p.m.' : '10:00 p.m.'}`}
                      </p>
                    </div>
                    {!soloMenores && (
                      <SwitchPref
                        activo={recibeMenores}
                        onToggle={handleRecibeMenores}
                        disabled={!emailNotifications}
                      />
                    )}
                  </div>

                  {menoresEncendidos && (
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                      gap: '12px'
                    }}>
                      {[
                        { valor: '13', titulo: '1:00 p.m.', detalle: 'Publicaciones de la mañana' },
                        { valor: '22', titulo: '10:00 p.m.', detalle: 'Resumen del día completo' }
                      ].map(({ valor, titulo, detalle }) => {
                        const elegida = horasMenores.includes(valor);
                        return (
                          <button
                            key={valor}
                            type="button"
                            role="checkbox"
                            aria-checked={elegida}
                            disabled={!emailNotifications}
                            onClick={() => handleHoraMenores(valor)}
                            style={{
                              borderRadius: '10px',
                              padding: '14px 16px',
                              border: elegida ? '2px solid #0f766e' : '2px solid #e5e7eb',
                              background: elegida ? '#edf5f1' : 'white',
                              cursor: emailNotifications ? 'pointer' : 'not-allowed',
                              textAlign: 'left',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '12px',
                              transition: 'border-color 0.2s ease, background 0.2s ease'
                            }}
                          >
                            <span style={{
                              width: '20px',
                              height: '20px',
                              borderRadius: '6px',
                              flexShrink: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              border: elegida ? '2px solid #0f766e' : '2px solid #d1d5db',
                              background: elegida ? '#0f766e' : 'white'
                            }}>
                              {elegida && <FaCheck style={{ fontSize: '0.625rem', color: 'white' }} />}
                            </span>
                            <span>
                              <span style={{
                                display: 'block',
                                fontWeight: '600',
                                fontSize: '0.875rem',
                                color: elegida ? '#0f4f47' : '#374151'
                              }}>
                                {titulo}
                              </span>
                              <span style={{ display: 'block', fontSize: '0.75rem', color: '#6b7280' }}>
                                {detalle}
                              </span>
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Contratos mayores — SEACE */}
                {verSeace && (
                <div className="np-card" style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '24px',
                  border: '1px solid #e5e7eb',
                  marginBottom: '24px',
                  opacity: emailNotifications ? 1 : 0.55
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '16px'
                  }}>
                    <div style={{ flex: 1 }}>
                      <h6 style={{
                        margin: 0,
                        marginBottom: '4px',
                        color: '#111827',
                        fontWeight: '600',
                        fontSize: '0.9375rem'
                      }}>
                        Contratos mayores — SEACE
                      </h6>
                      <p style={{ margin: 0, color: '#6b7280', fontSize: '0.8125rem' }}>
                        {seaceCorreo
                          ? 'Un correo al día con las convocatorias del SEACE'
                          : 'No recibirás el correo del SEACE'}
                      </p>
                    </div>
                    <SwitchPref
                      activo={seaceCorreo}
                      onToggle={() => setSeaceCorreo(actual => !actual)}
                      disabled={!emailNotifications}
                    />
                  </div>
                </div>
                )}

                {!emailNotifications && (
                  <div style={{
                    marginBottom: '24px',
                    background: '#f8fafc',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    display: 'flex',
                    gap: '10px'
                  }}>
                    <FaInfoCircle style={{
                      fontSize: '0.875rem',
                      color: '#64748b',
                      flexShrink: 0,
                      marginTop: '2px'
                    }} />
                    <p style={{ margin: 0, fontSize: '0.8125rem', color: '#475569', lineHeight: '1.4' }}>
                      Con las alertas por correo desactivadas no se envía nada. Estas preferencias
                      quedan guardadas y vuelven a aplicarse en cuanto las actives.
                    </p>
                  </div>
                )}

                {/* Tipos de contratación */}
                {emailNotifications && (
                  <div className="np-card" style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '24px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <div style={{ marginBottom: '16px' }}>
                      <h6 style={{ 
                        color: '#111827', 
                        fontWeight: '600', 
                        marginBottom: '4px',
                        fontSize: '0.9375rem'
                      }}>
                        Tipos de contratación
                      </h6>
                      <p style={{ 
                        margin: 0, 
                        color: '#6b7280', 
                        fontSize: '0.8125rem'
                      }}>
                        {selectedTypes.includes('ALL') 
                          ? 'Todos los tipos seleccionados' 
                          : `${selectedTypes.length} seleccionado${selectedTypes.length !== 1 ? 's' : ''}`}
                      </p>
                    </div>
                    
                    <div style={{ 
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                      gap: '12px'
                    }}>
                      {Object.entries(typeConfig).map(([type, config]) => (
                        <button
                          key={type}
                          onClick={() => handleTypeSelection(type)}
                          style={{
                            borderRadius: '10px',
                            padding: '16px',
                            border: selectedTypes.includes(type) 
                              ? `2px solid ${config.color}` 
                              : '2px solid #e5e7eb',
                            background: selectedTypes.includes(type)
                              ? `${config.color}08`
                              : 'white',
                            transition: 'all 0.2s ease',
                            cursor: 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '8px'
                          }}
                        >
                          <span style={{ fontSize: '1.5rem' }}>{config.icon}</span>
                          <span style={{ 
                            fontWeight: '500',
                            fontSize: '0.8125rem',
                            color: selectedTypes.includes(type) ? config.color : '#6b7280'
                          }}>
                            {config.label}
                          </span>
                          {selectedTypes.includes(type) && (
                            <FaCheck style={{ 
                              fontSize: '0.75rem', 
                              color: config.color 
                            }} />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'keywords' && emailNotifications && (
              <div style={{ maxWidth: '600px', margin: '0 auto' }}>
                <div className="np-card" style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '24px',
                  border: '1px solid #e5e7eb'
                }}>
                  <div style={{ marginBottom: '20px' }}>
                    <h6 style={{ 
                      color: '#111827', 
                      fontWeight: '600', 
                      marginBottom: '4px',
                      fontSize: '0.9375rem'
                    }}>
                      Palabras clave
                    </h6>
                    <p style={{ color: '#6b7280', fontSize: '0.8125rem', margin: '0 0 16px 0' }}>
                      Escribe los productos, servicios u obras que buscas. Cada frase se usará para filtrar las licitaciones que recibes.
                    </p>
                    <div style={{ margin: '0 0 16px', padding: '11px 13px', borderRadius: 9, background: '#f0f8f5', border: '1px solid #d7eae2', color: '#345b50', fontSize: '0.8125rem', lineHeight: 1.5 }}>
                      <strong>Ejemplo:</strong> escribe <strong>útiles de oficina</strong> y presiona <strong>+</strong> o <strong>Enter</strong> para agregarlo. Puedes añadir varias frases.
                    </div>

                    {/* Búsqueda general */}
                    <div 
                      onClick={() => {
                        if (generalKeywordSearch) {
                          setGeneralKeywordSearch(false);
                        } else {
                          setGeneralKeywordSearch(true);
                          setKeywords([]);
                          setSimilarSearch(false);
                        }
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px',
                        background: generalKeywordSearch ? '#edf5f1' : '#f9fafb',
                        borderRadius: '8px',
                        border: `1px solid ${generalKeywordSearch ? '#bfd8cb' : '#e5e7eb'}`,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        marginBottom: '16px'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={generalKeywordSearch}
                        onChange={() => {}}
                        style={{
                          width: '18px',
                          height: '18px',
                          cursor: 'pointer',
                          accentColor: '#0f766e'
                        }}
                      />
                      <span style={{ 
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        color: generalKeywordSearch ? '#0f4f47' : '#374151',
                        flex: 1
                      }}>
                        Buscar sin filtro de palabras clave
                      </span>
                    </div>
                  </div>

                  {/* Input para agregar palabras */}
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                    <Input
                      type="text"
                      placeholder="Ej: materiales de construcción"
                      value={keywordInput}
                      onChange={(e) => setKeywordInput(e.target.value)}
                      onKeyPress={handleKeywordKeyPress}
                      disabled={generalKeywordSearch}
                      style={{
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb',
                        fontSize: '0.875rem',
                        padding: '10px 14px',
                        flex: 1,
                        opacity: generalKeywordSearch ? 0.5 : 1
                      }}
                    />
                    <Button
                      aria-label="Agregar palabra clave"
                      onClick={handleAddKeyword}
                      disabled={!keywordInput.trim() || keywords.includes(keywordInput.trim()) || generalKeywordSearch}
                      style={{
                        borderRadius: '8px',
                        padding: '10px 16px',
                        background: '#0f766e',
                        border: 'none',
                        fontWeight: '500',
                        fontSize: '0.875rem',
                        opacity: (!keywordInput.trim() || keywords.includes(keywordInput.trim()) || generalKeywordSearch) ? 0.5 : 1
                      }}
                    >
                      <FaPlus style={{ fontSize: '0.75rem' }} />
                    </Button>
                  </div>

              {/* Búsqueda similar */}
{keywords.length > 0 && !generalKeywordSearch && (
  <div 
    onClick={(e) => {
      e.preventDefault();
      e.stopPropagation();
      setSimilarSearch(prev => !prev);
    }}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '12px',
      background: similarSearch ? '#fef3c7' : '#f9fafb',
      borderRadius: '8px',
      border: `1px solid ${similarSearch ? '#fde68a' : '#e5e7eb'}`,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      marginBottom: '16px'
    }}
  >
    <input
      type="checkbox"
      checked={similarSearch}
      onChange={(e) => {
        e.stopPropagation();
        setSimilarSearch(prev => !prev);
      }}
      onClick={(e) => e.stopPropagation()}
      style={{
        width: '18px',
        height: '18px',
        cursor: 'pointer',
        accentColor: '#f59e0b'
      }}
    />
    <span style={{ 
      fontSize: '0.875rem',
      fontWeight: '500',
      color: similarSearch ? '#92400e' : '#374151',
      flex: 1
    }}>
      Buscar palabras similares y sinónimos
    </span>
  </div>
)}

                  {/* Lista de palabras */}
                  {keywords.length > 0 ? (
                    <div>
                      <p style={{ 
                        fontSize: '0.75rem',
                        color: '#6b7280',
                        fontWeight: '500',
                        marginBottom: '10px'
                      }}>
                        {keywords.length} palabra{keywords.length !== 1 ? 's' : ''} configurada{keywords.length !== 1 ? 's' : ''}
                      </p>
                      <div style={{ 
                        display: 'flex', 
                        flexWrap: 'wrap', 
                        gap: '8px'
                      }}>
                        {keywords.map((keyword, index) => (
                          <div 
                            key={index}
                            style={{
                              background: '#edf5f1',
                              padding: '8px 12px',
                              borderRadius: '20px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              border: '1px solid #bfd8cb'
                            }}
                          >
                            <span style={{ 
                              fontSize: '0.8125rem', 
                              color: '#0f4f47', 
                              fontWeight: '500'
                            }}>
                              {keyword}
                            </span>
                            <button
                              onClick={() => handleRemoveKeyword(keyword)}
                              style={{
                                background: 'transparent',
                                border: 'none',
                                color: '#0f4f47',
                                cursor: 'pointer',
                                padding: '0',
                                width: '16px',
                                height: '16px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '16px',
                                fontWeight: 'bold',
                                lineHeight: '1'
                              }}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    !generalKeywordSearch && (
                      <div style={{
                        textAlign: 'center',
                        padding: '32px 20px',
                        background: '#f9fafb',
                        borderRadius: '8px',
                        border: '1px dashed #e5e7eb'
                      }}>
                        <div style={{ fontSize: '2rem', marginBottom: '8px', opacity: 0.5 }}>🔍</div>
                        <p style={{ color: '#6b7280', margin: 0, fontSize: '0.875rem' }}>
                          No hay palabras clave configuradas
                        </p>
                      </div>
                    )
                  )}
                </div>
              </div>
            )}

            {activeTab === 'filters' && emailNotifications && (
              <div style={{ maxWidth: '600px', margin: '0 auto' }}>
                <div className="np-card" style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '24px',
                  border: '1px solid #e5e7eb'
                }}>
                  <div style={{ marginBottom: '20px' }}>
                    <h6 style={{ 
                      color: '#111827', 
                      fontWeight: '600', 
                      marginBottom: '4px',
                      fontSize: '0.9375rem'
                    }}>
                      Ubicación de las oportunidades
                    </h6>
                    <p style={{ color: '#6b7280', fontSize: '0.8125rem', margin: '0 0 16px 0' }}>
                      Selecciona los departamentos de tu interés
                    </p>

                    {/* Búsqueda nacional */}
                    <div 
                      onClick={() => {
                        if (selectedDepartments.includes('ALL')) {
                          setSelectedDepartments([]);
                        } else {
                          setSelectedDepartments(['ALL']);
                        }
                        setDepartmentInput('');
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px',
                        background: selectedDepartments.includes('ALL') ? '#edf5f1' : '#f9fafb',
                        borderRadius: '8px',
                        border: `1px solid ${selectedDepartments.includes('ALL') ? '#bfd8cb' : '#e5e7eb'}`,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        marginBottom: '16px'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedDepartments.includes('ALL')}
                        onChange={() => {}}
                        style={{
                          width: '18px',
                          height: '18px',
                          cursor: 'pointer',
                          accentColor: '#0f766e'
                        }}
                      />
                      <span style={{ 
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        color: selectedDepartments.includes('ALL') ? '#0f4f47' : '#374151',
                        flex: 1
                      }}>
                        Buscar en todos los departamentos
                      </span>
                    </div>
                  </div>

                  {/* Input para departamentos */}
                  <div style={{ position: 'relative' }}>
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                      <Input
                        type="text"
                        aria-label="Buscar departamento"
                        placeholder="Nombre del departamento"
                        value={departmentInput}
                        onChange={(e) => setDepartmentInput(e.target.value)}
                        onKeyPress={handleDepartmentKeyPress}
                        disabled={selectedDepartments.includes('ALL')}
                        style={{
                          borderRadius: '8px',
                          border: '1px solid #e5e7eb',
                          fontSize: '0.875rem',
                          padding: '10px 14px',
                          flex: 1,
                          opacity: selectedDepartments.includes('ALL') ? 0.5 : 1
                        }}
                      />
                      <Button
                        aria-label="Agregar departamento"
                        onClick={handleAddDepartment}
                        disabled={!departmentInput.trim() || selectedDepartments.includes('ALL')}
                        style={{
                          borderRadius: '8px',
                          padding: '10px 16px',
                          background: '#0f766e',
                          border: 'none',
                          fontWeight: '500',
                          fontSize: '0.875rem',
                          opacity: (!departmentInput.trim() || selectedDepartments.includes('ALL')) ? 0.5 : 1
                        }}
                      >
                        <FaPlus style={{ fontSize: '0.75rem' }} />
                      </Button>
                    </div>

                    {/* Sugerencias */}
                    {getDepartmentSuggestions.length > 0 && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: '0',
                        right: '60px',
                        background: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)',
                        zIndex: 1000,
                        maxHeight: '200px',
                        overflowY: 'auto',
                        marginTop: '4px'
                      }}>
                        {getDepartmentSuggestions.map((dept, index) => (
                          <div
                            key={index}
                            onClick={() => {
                              setDepartmentInput(dept);
                              handleAddDepartment();
                            }}
                            style={{
                              padding: '10px 14px',
                              cursor: 'pointer',
                              borderBottom: index < getDepartmentSuggestions.length - 1 ? '1px solid #f3f4f6' : 'none',
                              transition: 'background-color 0.15s ease',
                              fontSize: '0.875rem',
                              fontWeight: '500',
                              color: '#374151'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                          >
                            {dept}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Lista de departamentos */}
                  {selectedDepartments.length > 0 && !selectedDepartments.includes('ALL') ? (
                    <div style={{ marginTop: '16px' }}>
                      <p style={{ 
                        fontSize: '0.75rem',
                        color: '#6b7280',
                        fontWeight: '500',
                        marginBottom: '10px'
                      }}>
                        {selectedDepartments.length} departamento{selectedDepartments.length !== 1 ? 's' : ''} seleccionado{selectedDepartments.length !== 1 ? 's' : ''}
                      </p>
                      <div style={{ 
                        display: 'flex', 
                        flexWrap: 'wrap', 
                        gap: '8px'
                      }}>
                        {selectedDepartments.map((dept, index) => (
                          <div 
                            key={index}
                            style={{
                              background: '#edf5f1',
                              padding: '8px 12px',
                              borderRadius: '20px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              border: '1px solid #bfd8cb'
                            }}
                          >
                            <span style={{ 
                              fontSize: '0.8125rem', 
                              color: '#0f4f47', 
                              fontWeight: '500'
                            }}>
                              {dept}
                            </span>
                            <button
                              onClick={() => handleRemoveDepartment(dept)}
                              style={{
                                background: 'transparent',
                                border: 'none',
                                color: '#0f4f47',
                                cursor: 'pointer',
                                padding: '0',
                                width: '16px',
                                height: '16px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '16px',
                                fontWeight: 'bold',
                                lineHeight: '1'
                              }}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    !selectedDepartments.includes('ALL') && (
                      <div style={{
                        textAlign: 'center',
                        padding: '32px 20px',
                        background: '#f9fafb',
                        borderRadius: '8px',
                        border: '1px dashed #e5e7eb',
                        marginTop: '16px'
                      }}>
                        <div style={{ fontSize: '2rem', marginBottom: '8px', opacity: 0.5 }}>🗺️</div>
                        <p style={{ color: '#6b7280', margin: 0, fontSize: '0.875rem' }}>
                          No hay departamentos seleccionados
                        </p>
                      </div>
                    )
                  )}
                </div>
              </div>
            )}

            {/* Mensaje cuando notificaciones están desactivadas */}
            {!emailNotifications && (activeTab === 'keywords' || activeTab === 'filters') && (
              <div style={{
                textAlign: 'center',
                padding: '48px 20px',
                background: 'white',
                borderRadius: '12px',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{ fontSize: '3rem', marginBottom: '16px', opacity: 0.3 }}>🔔</div>
                <h5 style={{ color: '#111827', fontWeight: '600', marginBottom: '8px', fontSize: '1rem' }}>
                  Notificaciones Desactivadas
                </h5>
                <p style={{ color: '#6b7280', margin: '0 0 20px 0', fontSize: '0.875rem' }}>
                  Activa las notificaciones para configurar estos filtros
                </p>
                <Button
                  onClick={() => setActiveTab('notifications')}
                  style={{
                    borderRadius: '8px',
                    padding: '10px 24px',
                    background: '#0f766e',
                    border: 'none',
                    fontWeight: '500',
                    fontSize: '0.875rem'
                  }}
                >
                  Ir a Notificaciones
                </Button>
              </div>
            )}
          </div>
        </ModalBody>

        <ModalFooter style={{ 
          background: 'white',
          borderTop: '1px solid #f3f4f6',
          padding: '20px 32px'
        }}>
          <Button 
            color="secondary" 
            outline
            onClick={togglePreferencesModal}
            disabled={saving}
            style={{ 
              borderRadius: '8px',
              fontWeight: '500',
              padding: '10px 24px',
              border: '1px solid #e5e7eb',
              color: '#6b7280',
              fontSize: '0.875rem'
            }}
          >
            Cancelar
          </Button>
          <Button 
            color="primary"
            onClick={handleSavePreferences}
            disabled={saving}
            style={{ 
              borderRadius: '8px',
              fontWeight: '500',
              padding: '10px 24px',
              background: '#0f766e',
              border: 'none',
              opacity: saving ? 0.7 : 1,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '0.875rem'
            }}
          >
            {saving ? (
              <>
                <FaSpinner className="fa-spin" style={{ fontSize: '0.875rem' }} />
                Guardando...
              </>
            ) : (
              <>
                <FaSave style={{ fontSize: '0.875rem' }} />
                Guardar Cambios
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Mi cuenta: nombre, empresa y contraseña. Al guardar se recarga el
          perfil para que el menú y la cabecera muestren el nombre nuevo. */}
      <MiCuenta31
        isOpen={cuentaModalOpen}
        toggle={toggleCuentaModal}
        user={user}
        perfil={formattedData}
        onPerfilGuardado={loadUserData}
      />

      {/* Estilos */}
      <style>{`
        .user-dropdown-toggle:hover {
          border-color: #d1d5db !important;
          box-shadow: 0 2px 4px rgba(0,0,0,0.08) !important;
        }

        .dropdown-item-hover:hover {
          background: #f9fafb !important;
        }

        ::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }

        ::-webkit-scrollbar-track {
          background: #f3f4f6;
        }

        ::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 10px;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }
      `}</style>
    </>
  );
};

export default UserDropdown;
